import styled from '@emotion/styled';
import { AjvError, FieldTemplateProps, IChangeEvent, ISubmitEvent, ThemeProps, UiSchema, withTheme } from '@rjsf/core';
import React from 'react';
import { BaseInput } from './BaseInput';
import { SelectWidget } from './SelectWidget';
import { mapSchemaToErrorMetadata } from './errorHandling';
import { CarrotJsonSchema, SchemaErrorMetadata } from './types';

interface JsonSchemaFormProps {
    uiSchema?: UiSchema;
    schema: CarrotJsonSchema;
    formData?: unknown;
    onSubmit?: (e: ISubmitEvent<unknown>) => void;
    onChange?: (e: IChangeEvent<unknown>) => void;
    children?: React.ReactNode;
    usePlaceholders?: boolean;
    overrideTitle?: (originalTitle: string) => string;
    overrideSelectLabel?: (fieldName: string, labelValue: string | number) => string | number;
    overrideErrorMessage?: (originalErrorMessage: string, errorMetadata: SchemaErrorMetadata) => string;
}

export function JsonSchemaForm(props: JsonSchemaFormProps) {
    const {
        children,
        uiSchema,
        formData,
        onSubmit,
        onChange,
        schema,
        usePlaceholders,
        overrideTitle,
        overrideSelectLabel,
        overrideErrorMessage,
    } = props;

    const formTheme: ThemeProps = {
        noHtml5Validate: true,
        transformErrors: _transformErrors,
        onError: _onError,
        onSubmit: _onSubmit,
        onChange: _onChange,
        FieldTemplate: CustomFieldTemplate,
        uiSchema: uiSchema,
        widgets: {
            BaseInput: (props) => (
                <BaseInput
                    {...props}
                    usePlaceHolders={usePlaceholders}
                    overrideTitle={overrideTitle}
                    overrideErrorMessage={overrideErrorMessage ? _overrideErrorMessage : undefined}
                />
            ),
            SelectWidget: (props) => (
                <SelectWidget
                    {...props}
                    overrideTitle={overrideTitle}
                    overrideSelectLabel={overrideSelectLabel}
                    overrideErrorMessage={overrideErrorMessage ? _overrideErrorMessage : undefined}
                />
            ),
        },
    };
    const ThemedForm = withTheme(formTheme);

    const formDataWithNullsRemoved = removeNullsFromFormData(formData);

    function _transformErrors(errors: AjvError[]): AjvError[] {
        for (const error of errors) {
            error.message = error.name;
        }

        return errors;
    }

    function _onError(error: unknown) {
        console.log(error);
    }

    function _overrideErrorMessage(originalMessage: string, carrotSchema: CarrotJsonSchema) {
        return overrideErrorMessage(originalMessage, mapSchemaToErrorMetadata(carrotSchema, overrideTitle));
    }

    function _onSubmit(event: ISubmitEvent<unknown>) {
        onSubmit && onSubmit(event);
    }

    function _onChange(event: IChangeEvent<unknown>) {
        onChange && onChange(event);
    }

    return (
        <CustomCss>
            <ThemedForm
                {...{
                    action: '#',
                    schema,
                    omitExtraData: true,
                    formData: formDataWithNullsRemoved,
                    showErrorList: false,
                }}
            >
                {children}
            </ThemedForm>
        </CustomCss>
    );
}

function CustomFieldTemplate(props: FieldTemplateProps) {
    const { children, uiSchema } = props;
    const { minWidth, widthPercent } = uiSchema?.props ?? {};

    const style = {
        width: widthPercent ? `calc(${widthPercent} - 6px)` : '100%',
        marginBottom: 16,
        minWidth: minWidth,
        flexGrow: 1,
    };

    return <div style={style}>{children}</div>;
}

function removeNullsFromFormData(formData: any) {
    if (!formData) {
        return undefined;
    }

    return Object.keys(formData).reduce((prev, key: string) => {
        prev[key] = formData[key] === null ? undefined : formData[key];

        return prev;
    }, {} as any);
}

const CustomCss = styled.div`
    width: 100%;
    display: block;

    form {
        width: 100%;

        fieldset {
            display: flex;
            column-gap: 10px;
            flex-direction: row;
            flex-wrap: wrap;
        }
    }
`;
